import { store } from '@/store'
import AppSyncNotification from '@/types/AppSyncNotification.interface'
import Analytics from '@/services/Analytics'
import AppSync from '@/services/AppSync'
import Device from '@/services/Device'
import DocumentTitle from '@/services/DocumentTitle'
import Intercom from '@/services/Intercom'
import Intuit from '@/services/Intuit'
// import LogRocket from '@/services/LogRocket'
import ProfitWell from '@/services/ProfitWell'
import Tenant from '@/types/Tenant.interface'
import UserNotifications from '@/services/UserNotifications'
import UserVerification from '@/services/UserVerification'
import User from '@/types/User.interface'
// import ViralLoops from '@/services/ViralLoops'
import Websockets from '@/services/Websockets'
import Xero from '@/services/Xero'
import { randomKey } from '@/utility/helpers'


export default {
    //  Occurs when the client receives an AppSync notification from the server
    onAppSyncNotificationReceived(notification: AppSyncNotification): void {
        store.getters.appDebug && console.log('EVENT: AppSyncNotificationReceived')

        //  AppSync service subscribes to this event
        AppSync.onAppSyncNotificationReceived(notification)

        //  Intuit service subscribes to this event
        Intuit.onAppSyncNotificationReceived(notification)

        //  Xero service subscribes to this event
        Xero.onAppSyncNotificationReceived(notification)
    },
    //  Occurs when the root Vue application is mounted()
    onApplicationLoaded(): void {
        store.getters.appDebug && console.log('EVENT: ApplicationLoaded')

        //  Analytics subscribes to this event
        Analytics.onApplicationLoaded()

        //  The DocumentTitle service subscribes to this event
        DocumentTitle.onApplicationLoaded()

        //  The Device service subscribes to this event
        Device.onApplicationLoaded()

        //  Intercom subscribes to this event
        Intercom.onApplicationLoaded()
    },
    //  Occurs when the user's jwt is set in the store
    onAuthTokenSet(token: string): void {
        store.getters.appDebug && console.log('EVENT: AuthTokenSet')

        //  Device service subscribes to thie event
        Device.onAuthTokenSet(token)
    },
    //  Occurs when the user's 2FA is set in the store
    onTwoFactorTokenSet(twoFactorToken: string): void {
        store.getters.appDebug && console.log('EVENT: TwoFactorTokenSet')

        //  Device service subscribes to thie event
        Device.onTwoFactorTokenSet(twoFactorToken)
    },
    //  Occurs when the client recevies a Browser notification from the server
    onBrowserNotificationReceived(notification): void {
        store.getters.appDebug && console.log('EVENT: BrowserNotificationReceived')

        var imageVar = notification.image;
        var text = notification.text;
        var title = notification.title;
        var browserNotification = new Notification(title, { body: text, icon: imageVar, badge: imageVar });

        //  Check if we are on the mobile app
        const isApp = store.getters.isApp

        //  We only play the inbound ding for new messages recieved 
        const playInboundNotifcation = !isApp && title.includes('New Message')

        //  If we are NOT on a mobile ap and this is a new inbound message, play the sound
        if (playInboundNotifcation) window.sounds.play('inboundDing')

        store.getters.appDebug && console.log(browserNotification);
    },
    //  Occurs when pagination is pushed into 'channels' in vuex store
    onChannelsPushed(): void {
        //  DocumentTitle subscribes to this event
        DocumentTitle.onChannelsPushed()
    },
    //  Occurs when the client receives a messageReceived AppSync notification (occurs at end of AppSync handling)
    onMessageReceived(): void {
        //  DocumentTitle subscribes to this event
        DocumentTitle.onMessageReceived()
    },
    //  Occurs when the app encounters the unique HTTP response for a Suspended Tenant
    onTenantSuspendedHttpResponse(message: string): void {
        //  Get the current list of notification popups
        const popups = store.getters.notificationPopup

        //  Check if a suspension notification already exists
        const exists = popups.find(x => x.text === message)

        //  If the suspension notification already exists, do nothing
        if (exists) return

        //  Else, pop notification
        store.commit('notificationPopupOpen', {
            title: 'Account Suspension',
            text: message,
            icon: 'bi-emoji-dizzy-fill',
            id: randomKey(),
            error: true,
            button: {
                name: 'Contact Support',
                click: 'popSupportMessenger'
            }
        })
    },
    //  Occurs when the current tenant is set to null in the store
    onTenantRemoved(tenant: Tenant): void {
        store.getters.appDebug && console.log('EVENT: Tenant Removed')

        //  AppSync service subscribes to this event
        AppSync.onTenantRemoved()

        //  Websockets service subscribes to this event
        Websockets.onTenantRemoved(tenant)

        //  DocumentTitle service subscribes to this event
        DocumentTitle.onTenantRemoved()
    },
    //  Occurs when a NEW tenant is set in the store (will not get fired with updating an existing tenant)
    onTenantSet(tenant: Tenant): void {
        store.getters.appDebug && console.log('EVENT: Tenant Set', tenant)

        //  AppSync service subscribes to this event
        AppSync.onTenantSet(tenant)

        //  Websockets service subscribes to this event
        Websockets.onTenantSet(tenant)

        //  DocumentTitle service subscribes to this event
        DocumentTitle.onTenantSet()

        //  Analytics service subscribes to this event
        Analytics.onTenantSet(tenant)

        //  Device service subscribes to this event
        Device.onTenantSet(tenant)

        //  UserVerification subscribes to this event
        UserVerification.onTenantSet(tenant)

        //  Intercom service subscribes to this event
        Intercom.onTenantSet(tenant)

        //  LogRocket subscribes to this event
        // LogRocket.onTenantSet()

        //  ViralLoops subscribes to this event
        // ViralLoops.onTenantSet(tenant)
    },
    //  Occurs when the app finishes the 'login' action in the store (this is different than simply setting the User in the store)
    onUserLogin(user: User): void {
        store.getters.appDebug && console.log('EVENT: User Login', user)

        //  AppSync Service subscribes to this event
        AppSync.onUserLogin(user)

        //  Device service subscribes to this event
        Device.onUserLogin(user)

        //  LogRocket subscribes to this event
        // LogRocket.onUserLogin(user)

        //  Analytics service subsrcibes to this event
        Analytics.onUserLogin(user)

        //  Profit Well subscribes to this event
        ProfitWell.onUserLogin(user)
    },
    //  Occurs when the app finished the 'logout' action in the store.
    onUserLogout(): void {
        store.getters.appDebug && console.log('EVENT: UserLogout')

        //  Device service subscribes to this event
        Device.onUserLogout()

        //  Websockets service subscribes to this event
        Websockets.onUserLogout()

        //  Intuit service subsrcibes to this event
        Intuit.onUserLogout()

        //  AppSync serrvice subscribes to this event
        AppSync.onUserLogout()
    },
    //  Occurs when a new user completes registration (via RegisterAuthenticate OR RegisterUser workflows)
    onUserRegistered(user: User, data: object = {}): void {
        store.getters.appDebug && console.log('EVENT: UserRegistered')

        //  Analytics subscribes to this event
        Analytics.onUserRegistered(user, data)

        //  Intercom subscribes to this event
        Intercom.onUserRegistered(user)

        //  LogRocket subscribes to this event
        // LogRocket.onUserRegistered(user)
    },
    //  Occurs when the client receives a User notification from the server
    onUserNotificationReceived(notification) {
        store.getters.appDebug && console.log('EVENT: UserNotificationReceived')

        //  AppSync service subscribes to this event
        AppSync.onUserNotificationReceived(notification)

        //  UserNotification service subscribes to this event
        UserNotifications.onUserNotificationReceived(notification)
    }
}
